import React, { useEffect } from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import LoadingSpinner from '../ui/LoadingSpinner';
import { supabase } from '../../lib/supabase';

const ProtectedRoute: React.FC = () => {
  const { user, initialized, initialize } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const initAuth = async () => {
      if (!initialized) {
        await initialize();
      }
    };

    initAuth();
  }, [initialized, initialize]);

  useEffect(() => {
    const checkUserRole = async () => {
      if (!user?.id) {
        if (location.pathname !== '/login' && 
            location.pathname !== '/register' && 
            location.pathname !== '/registrationform' && 
            location.pathname !== '/houses/performance') {
          navigate('/login');
        }
        return;
      }

      try {
        const { data: userRoles } = await supabase
          .from('user_roles')
          .select(`
            roles!inner(
              role_name
            )
          `)
          .eq('user_id', user?.id);

        const hasAccess = userRoles?.some(ur => 
          ur.roles.role_name === 'admin' || 
          ur.roles.role_name === 'manager' ||
          ur.roles.role_name === 'referee' ||
          ur.roles.role_name === 'viewer' ||
          ur.roles.role_name === 'registrering'
        ) ?? false;

        if (hasAccess) {
          const isReferee = userRoles?.some(ur => ur.roles.role_name === 'referee');
          const isAdmin = userRoles?.some(ur => ur.roles.role_name === 'admin');
          const isManager = userRoles?.some(ur => ur.roles.role_name === 'manager');
          const isViewer = userRoles?.some(ur => ur.roles.role_name === 'viewer');
          const isRegistration = userRoles?.some(ur => ur.roles.role_name === 'registrering');

          // If user is only a referee (not admin or manager), restrict to results page
          if (isReferee && !isAdmin && !isManager && !isViewer && !isRegistration && location.pathname !== '/competitions/results') {
            navigate('/competitions/results');
          }
          
          // If user is only a registration user, restrict to participants and family connections pages
          if (isRegistration && !isAdmin && !isManager && !isReferee && !isViewer && 
              !location.pathname.startsWith('/participants') && 
              location.pathname !== '/login') {
            navigate('/participants');
          }
          
          // If user is only a viewer, they should only have read access to certain pages
          // but we don't need to redirect them from those pages
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error checking user role:', error);
      }
    };

    if (user?.id) {
      checkUserRole();
    }
  }, [user?.id, location.pathname, navigate]);

  if (!initialized) {
    return <LoadingSpinner />;
  }
  
  // Allow access to public routes without authentication
  if (location.pathname === '/register' || 
      location.pathname === '/registrationform' || 
      location.pathname === '/houses/performance') {
    return <Outlet />;
  }
  
  if (!user && location.pathname !== '/login') {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;